import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { LogOut, Menu, Moon, Sun } from 'lucide-react'
import BrandLogo from '@/components/brand-logo'
import ConfirmDialog from '@/components/confirm-dialog'
import { navigation } from '@/navigation'
import { useAuth } from '@/utility/context/auth-context'
import { useTheme } from '@/utility/context/theme-context'
import { useNewVersion } from '@/utility/use-new-version'
import { orDash } from '@/utility/Utils'
import styles from './style.module.css'

const AppShell = () => {
  const { can, signOut, user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSignOutOpen, setIsSignOutOpen] = useState(false)
  const [isAtualizacaoDispensada, setIsAtualizacaoDispensada] = useState(false)
  const { temVersaoNova } = useNewVersion()

  const handleSignOut = () => {
    setIsSignOutOpen(false)
    signOut()
    void navigate('/login', { replace: true })
  }

  const groups = navigation
    .map((group) => ({ ...group, items: group.items.filter((item) => can(item.acao)) }))
    .filter((group) => group.items.length > 0)

  return (
    <div className={styles.layout}>
      <aside className={classNames(styles.sidebar, { [styles.sidebarOpen]: isSidebarOpen })}>
        <div className={styles.brand}>
          <BrandLogo className={styles.logo} />
        </div>

        <nav className={styles.nav}>
          {groups.map((group) => (
            <div className={styles.group} key={group.id}>
              <span className={styles.groupTitle}>{group.title}</span>
              {group.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    classNames(styles.navLink, { [styles.navLinkActive]: isActive })
                  }
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <item.icon size={18} />
                  <span>{item.title}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setIsSidebarOpen((current) => !current)}
            aria-label="Alternar menu"
          >
            <Menu size={20} />
          </button>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div className={styles.user}>
              <span className={styles.userName}>{orDash(user?.nomeUsuario)}</span>
              <span className={styles.userRole}>{orDash(user?.perfil)}</span>
            </div>

            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setIsSignOutOpen(true)}
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>

      {isSidebarOpen ? (
        <div
          className={styles.backdrop}
          onClick={() => setIsSidebarOpen(false)}
          role="presentation"
        />
      ) : null}

      <ConfirmDialog
        isOpen={isSignOutOpen}
        toggle={() => setIsSignOutOpen(false)}
        title="Sair do Admin IntegraDoor"
        message="Você será desconectado e precisará entrar novamente para continuar o atendimento."
        confirmLabel="Sair"
        cancelLabel="Continuar conectado"
        onConfirm={handleSignOut}
      />

      <ConfirmDialog
        isOpen={temVersaoNova && !isAtualizacaoDispensada}
        toggle={() => setIsAtualizacaoDispensada(true)}
        title="Nova versão disponível"
        message="Uma versão nova do Admin IntegraDoor foi publicada. Recarregue a página para usá-la; o que estiver preenchido em tela será perdido."
        confirmLabel="Recarregar agora"
        cancelLabel="Mais tarde"
        tone="warning"
        onConfirm={() => window.location.reload()}
      />
    </div>
  )
}

export default AppShell
